(async () => {
  const {
    updateURLParams,
    historyPush,
    parseHTML
  } = await import(window.theme.modules.utils);
  const baseComponent = await import(window.theme.modules.baseComponent);

  customElements.define('product-form', class extends baseComponent.default {
    elements = {
      form: 'form',
      addButton: '[data-add-button]',
      buyButton: '[data-buy-button]',
      soldOutButton: '[data-sold-out-button]',
      input: '[data-variant-id-input]',
      sellingPlanInput: 'input[name="selling_plan"]'
    };
    async render() {
      this._enableInput();
      const packageData = this.querySelector('[data-vt-package-components]');
      if (packageData) {
        try { this.packageComponents = JSON.parse(packageData.textContent); }
        catch { this.packageComponents = {}; }
        this._updatePackageComponents();
        this.$form.addEventListener('formdata', event => {
          this._updatePackageComponents();
          for (const field of this.querySelectorAll('[data-vt-components], [data-vt-package-sku], [data-vt-installation]')) {
            if (field.disabled) event.formData.delete(field.name);
            else event.formData.set(field.name, field.value);
          }
        });
      }
      this.cartSection = document.querySelector('main-cart');
      this.isProductPage = this.getAttribute('page-type') === 'product';
      this.listenTo('variantChange', (e, data) => {
        this._rerender(data);
        if (data.variant) {
          this._rerenderVariantInput(data)
        }
      });
      this.on('cartChange', (e, data) => {
        this._updateQtyStatus(data.variantId)
      }, false)
      if (this.hasAttribute('cart-popup'))
        this.$form.addEventListener('submit', this._handleSubmit.bind(this));
      
      if (this.$sellingPlanInput && this.hasAttribute('selling-plan-enable')) {
        this.currentSellingPlanId = this.querySelector('input[name="selling_plan"]').value;
        const {observeInputValueChange} = await import(window.theme.modules.sellingPlanUtil);
        observeInputValueChange(this.$sellingPlanInput, this._handleSellingPlanChange.bind(this));
      }
    };
    
    _handleSellingPlanChange(newSellingPlanId) {
      if (newSellingPlanId === this.currentSellingPlanId) 
        return;      
      this.currentSellingPlanId = newSellingPlanId;
      const { queryURL, currentURL } = updateURLParams({ 'selling_plan': this.currentSellingPlanId, 'variant': this.getAttribute('current-variant') }, this.sectionId);
      if (this.isProductPage)
        historyPush(currentURL);
      this._fetchPageWithVariant(queryURL, html => {
        this.forwardEvent('sellingPlanChange', {
          html: html
        })
      }); 
    };

    _enableInput() {
      this.$input.disabled = false;
    }
    
    _fetchPageWithVariant(url, onResponse) {
      return fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = parseHTML(responseText)
        onResponse(html);
      });
    };

    _rerender(data) {
      const isHiddenAddBtn = data.html.querySelector('[data-add-button]').hasAttribute('hidden');
      const unavailableButton = data.html.querySelector('[data-sold-out-button]');
      const isHiddenSoldOutBtn = unavailableButton.hasAttribute('hidden');
      this.$soldOutButton.toggleAttribute('data-cart-limit-reached', unavailableButton.hasAttribute('data-cart-limit-reached'));
      this.$soldOutButton.querySelector('[class~="#button-text"]').textContent = unavailableButton.querySelector('[class~="#button-text"]').textContent;
      this.$addButton.toggleAttribute('hidden', isHiddenAddBtn);
      this.$buyButton?.toggleAttribute('hidden', isHiddenAddBtn);
      this.$soldOutButton.toggleAttribute('hidden', isHiddenSoldOutBtn);
    };
    
    _rerenderVariantInput(data) {
      this.setAttribute('current-variant', `${data.variant.id}`);
      const value = data.html.querySelector(this.elements.input).value;
      this.$input.value = value;
      this._updatePackageComponents();
    };
    
    _updatePackageComponents() {
      if (!this.packageComponents) return;
      const item = this.packageComponents[this.$input.value];
      const valid = item && Array.isArray(item.components) && item.components.length > 0 &&
        item.components.every(component => typeof component.sku === 'string' && component.sku.trim() && Number.isInteger(component.quantity) && component.quantity > 0) &&
        typeof item.sku === 'string' && item.sku.trim() && Number.isFinite(item.installation) && item.installation >= 0;
      const fields = [
        ['[data-vt-components]', valid ? JSON.stringify(item.components) : ''],
        ['[data-vt-package-sku]', valid ? item.sku : ''],
        ['[data-vt-installation]', valid ? String(item.installation) : '']
      ];
      for (const [selector, value] of fields) {
        const field = this.querySelector(selector);
        if (!field) continue;
        field.value = value;
        field.disabled = !valid;
      }
    }

    _handleSubmit(e) {
      e.preventDefault();
      this.formData = new FormData(e.target);
      this._addToCartRequest();
    };

    _addToCartRequest() {
      if (this._requestPending) return;
      this._requestPending = true;
      this.querySelector('.vt-cart-error')?.remove();
      this._setLoading(true);
      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          "Accept": "application/javascript"
        },
        body: this.formData
      })
      .then(async res => {
        const data = await res.json();
        if (!res.ok || data.status >= 400) throw new Error(data.description || this.dataset.errorMessage);
        return data;
      })
      .then(() => {
        this.trigger('updateCart', {
          callback: () => {
            this._setLoading(false);
            this._updateQtyStatus(this.formData.get('id'));
            if(this.cartSection) {
              this.cartSection.update();
            } else {
              this.trigger('openPopup', { target: 'CART' });
            }
          }
        })
      })
      .catch(error => {
        this._setLoading(false);
        const message = document.createElement('p');
        message.className = 'vt-cart-error';
        message.setAttribute('role', 'alert');
        message.textContent = error.message === 'Failed to fetch' ? this.dataset.errorMessage : (error.message || this.dataset.errorMessage);
        this.append(message);
      });
    };

    _updateQtyStatus(variantId) {
      const selectedVariantId = this.getAttribute('current-variant');
      const url = `${this.getAttribute('product-url')}?variant=${selectedVariantId}&section_id=${this.getAttribute('section-id')}`;
      this._fetchPageWithVariant(url, html => {
        if (this.getAttribute('current-variant') !== selectedVariantId) return;
        this.trigger('qtyStatusUpdate', {html: html, variantId: selectedVariantId})
        this._rerender({html: html})
      })
    };
    
    _setLoading(status) {
      if (!status) this._requestPending = false;
      status 
        ? this.$addButton.setAttribute('loading', '')
        : this.$addButton.removeAttribute('loading');
      // status ? this.$addButton.setAttribute('disabled', true) :
      //   this.$addButton.removeAttribute('disabled');
      // this.$addButton.textContent = status ? window.theme.i18n.loading : window.theme.i18n.addToCart;
    };
  });
})();
